import Toggle from '@/components/Toggle';
import LexicalInput from '@/components/lexicalInput/LexicalInput';
import { BiText, PiBracketsCurly } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import RightPaneFieldV2 from '@/components/v2/RightPaneFieldV2';
import useExternalUpdate from '@/hooks/useExternalUpdate.js';
import { action } from '@/lib/api.js';
import { DeleteIcon } from '@/lib/constants/icons';
import getLookupMethod from '@/lib/getLookupMethod.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { store } from '@/redux/lib/configureStore.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import { query } from '@/redux/selectors/querySelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources';
import appActions from '@/screens/apps/redux/actions/appActions';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { randomId } from '@shared/utils/randomId.js';
import React, { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import suggestions from '../v2/suggestionsFloatingPane/suggestionContexts';
import ArgumentFilePopover from './ArgumentFilePopover.jsx';
import ArgumentSelect from './ArgumentSelect';
import handleEnterRawObject from './argumentRawObjectButton/handleEnterRawObject';

const dataTypes = {
  string: 'lexical',
  number: 'lexical',
  boolean: 'lexical',
  'boolean(text)': 'lexical',
  'boolean(toggle)': 'toggle',
  date: 'lexical',
  list: 'select',
  file: 'lexical',
  array: 'lexical',
  object: 'lexical'
};

const dataTypesPlaceholders = {
  boolean: 'True/False',
  file: 'URL, Base64 or Binary'
};

const computeTimezone = ({ organizationTz, credentialTz, argumentTz }) => {
  const order = [
    { value: argumentTz, label: 'argument' },
    { value: credentialTz, label: 'credential' },
    { value: organizationTz, label: 'organization' }
  ];
  const tz = order.find((n) => n?.value);
  return tz ?? 'default';
};

const ArgumentInput = memo(({ id: propId, argument, index, definition, dataSource, dataSourcePath, dataSavePath, disabled }) => {
  const [customComponent, setCustomComponent] = useState();
  const [updateKey, setUpdateKey] = useState(Math.random());
  const id = useRef(propId || randomId());

  const stepId = useArgSelector(query, 'stepId');
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const activeArgumentId = useArgSelector(selectors.query, 'argumentId');
  const lookupMethod = useMemo(() => getLookupMethod(dataSource, true, argument?.dataType), [dataSource, argument?.dataType]);
  const { lookup, loading, reload } = lookupMethod({ id: argument?.id, lookupSettings: argument?.list });
  const stepDataSources = [argumentDataSources.workflowStep, argumentDataSources.workflowVariables, `${argumentDataSources.workflowStep}-customArguments`];
  const actions = stepDataSources.includes(dataSource) ? stepActions : appActions;

  const argumentValueTypeIsLexical = useSelector(memoizeSelector(`argumentValueType-${argument?.key}`, (state) => argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key]?.type === 'lexical'));
  const multipleArgValuesLength = useSelector((state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key)?.length);
  const argValue = useSelector(memoizeSelector(`argValue-${id}-${argument?.key}-${index}`, (state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index)));
  const argValueExists = useSelector((state) => !!argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index));
  const argValueValue = useSelector(memoizeSelector(`argValueValue-${id}-${argument?.key}-${index}`, (state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index)?.value));
  const argValueTimezone = useSelector((state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index)?.timezone);
  const argValueType = useSelector((state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index)?.type);
  const argValueTypeIsLexical = useSelector((state) => argumentSelectors[dataSource].activeArgumentValue(state, argument?.key, index)?.type === 'lexical');
  const argAppSlug = useSelector((state) => argumentSelectors[dataSource].activeApp(state)?.slug);
  const organizationTimezone = useSelector((state) => selectors.activeCompany(state)?.timezone);
  const credentialTimezone = useSelector((state) => argumentSelectors[dataSource].activeCredentials(state)?.secrets?.timezone);
  const fileName = useSelector(memoizeSelector(`fileName-${argument?.key}`, (state) => argumentSelectors[dataSource].activeOptions(state)?.fileName?.[argument?.key]));
  const isTrigger = useSelector(workflowScreenSelectors.activeWorkflowStepIsTrigger);
  const stepAppId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.id);
  const isTriggerCreated = useSelector((state) => !!workflowScreenSelectors.activeWorkflowStepAppTriggerData(state)?.webhookId);
  const activeArgument = useSelector(appScreenSelectors.activeAppActionArgument);

  // This is specific to Netsuite, since the label and value can be totally different
  const lookupOptions = useMemo(() => {
    if (argAppSlug === 'netsuite' && argument?.key === 'recordType') {
      return lookup?.map((item) => ({ ...item, tag: item?.value }));
    }
    return lookup;
  }, [lookup, argAppSlug, argument?.key]);

  const timezone =
    dataSource === argumentDataSources.workflowStep &&
    argument?.dataType === 'date' &&
    computeTimezone({
      organizationTz: organizationTimezone,
      credentialTz: credentialTimezone,
      argumentTz: argValueTimezone
    });

  const dispatch = useDispatch();
  const allArgumentDefs = useSelector(dataSource === argumentDataSources.workflowStep ? workflowScreenSelectors.activeWorkflowStepAppAction : appScreenSelectors.activeAppAction)?.arguments || [];

  useExternalUpdate({
    key: `lexicalInput-${argument?.key}`,
    callback: async () => {
      setUpdateKey(Math.random());
    }
  });

  const isFile = argument?.dataType === 'file';
  const isRawObject = ['array', 'object'].includes(argument?.dataType) && argumentValueTypeIsLexical;
  const isNotArrayOfObjects = argument?.dataType === 'array' && argument?.itemDataType && argument?.itemDataType !== 'object';
  const hasMultipleValues = !isRawObject && (argument?.multipleValues?.isActive === true || isNotArrayOfObjects);
  index = hasMultipleValues ? index : undefined;

  const dataTypeString = argument.dataType === 'boolean' ? `${argument?.dataType}(${argument.format || 'toggle'})` : `${argument.dataType}`;
  const itemDataTypeString = `${argument.itemDataType}`;
  const customComponentType = argument?.component ? 'custom' : undefined;
  const isUsingListVariables = argValueTypeIsLexical && argument?.dataType === 'list' ? 'lexical' : undefined;
  const isUsingBooleanVariables = argValueTypeIsLexical && argument?.dataType === 'boolean' ? 'lexical' : undefined;
  const componentType = customComponentType || isUsingListVariables || isUsingBooleanVariables || dataTypes[dataTypeString] || dataTypes[itemDataTypeString] || argValueType;
  const placeholder = argument?.placeholder || dataTypesPlaceholders[argument?.dataType] || 'Text';
  const rootPath = dataSavePath ? `${dataSavePath}.arguments` : dataSourcePath;

  const activeArgumentDef = allArgumentDefs?.find((n) => n.id === activeArgumentId);
  const lookupActionId = activeArgumentDef?.list?.lookupActionId; // This is NetSuite's action instance modal on the app screen. For example, Delete Customer needs to run List Customers in its list argument, and List Customers needs to run with its own arguments, not using the ones from Delete Customer.

  const initialValue = useMemo(() => {
    if (argument?.dataType === 'list' && componentType !== 'lexical' && loading) {
      return undefined;
    } else if (componentType === 'default') {
      return argValueValue;
    } else {
      return argValue || argument.defaultValue || '';
    }
  }, [argument?.dataType, componentType, loading, argValue, argValueValue, argument?.defaultValue]);

  const booleanToggleInitialValue = typeof initialValue === 'object' ? initialValue?.value : initialValue === 'false' ? false : initialValue === 'true' ? true : initialValue;
  const showTrash = index < multipleArgValuesLength || isRawObject;

  const writeData = (key = null, value) => {
    const fullPath = key === null ? rootPath : `${rootPath}.["${key}"]`;
    actions.set(fullPath, value);

    // if it is a trigger, we wait for the sync to be ready, and then run step action to update the trigger
    if (isTrigger && workflowId && isTriggerCreated) {
      action('TRIGGER_WORKFLOW_UPDATE', { workflowId, stepId, stepAppId });
    }
  };

  const handleChange = (value) => {
    const val = {
      timezone: value?.timezone ? value.timezone : undefined,
      value: value?.value ? value.value : value,
      label: value?.label ? value.label : undefined,
      type: value?.type || componentType,
      ...(value?.sourceArgumentId ? { sourceArgumentId: value.sourceArgumentId } : {})
    };

    let actualValue = val;

    if (value?.type === 'select' && value?.value === null) {
      handleDelete();
      return;
    }

    if (index !== undefined) {
      const state = store.getState();
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
      actualValue[index] = { ...val, id: id.current };
    }

    writeData(argument?.key, actualValue);

    allArgumentDefs.forEach((argDef) => {
      if (Array.isArray(argDef.dependencies) && argDef.dependencies.includes(argument?.id)) {
        dispatch({ type: 'TRIGGER_DEPENDENT_LOOKUP_RELOAD', payload: { id: argDef.id } });
      }
    });
  };

  const handleUseVariables = () => {
    const val = { type: 'lexical', value: null, label: null };

    let actualValue = val;

    if (index !== undefined) {
      const state = store.getState();
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
      actualValue[index] = { ...val, id: id.current };
    }

    writeData(argument?.key, actualValue);
  };

  const handleUseOptions = () => {
    const val = { type: 'select', value: null, label: null };

    let actualValue = val;

    if (index !== undefined) {
      const state = store.getState();
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
      actualValue[index] = { ...val, id: id.current };
    }

    writeData(argument?.key, actualValue);
  };

  const handleUseVariablesForBoolean = () => {
    const val = { type: 'lexical', value: null };

    let actualValue = val;

    if (index !== undefined) {
      const state = store.getState();
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
      actualValue[index] = { ...val, id: id.current };
    }

    writeData(argument?.key, actualValue);
  };

  const handleUseToggle = () => {
    const val = { type: 'toggle', value: false };

    let actualValue = val;

    if (index !== undefined) {
      const state = store.getState();
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
      actualValue[index] = { ...val, id: id.current };
    }

    writeData(argument?.key, actualValue);
  };

  const handleToggleChange = (value) => {
    handleChange(!!value);
  };

  const handleTimezoneChange = useCallback(
    (value) => {
      actions.set(`arguments.["${argument?.key}"].timezone`, value);
    },
    [argument?.key]
  );

  const handleFileNameChange = (value) => {
    actions.set(`options.fileName.["${argument?.key}"]`, value);
  };

  const handleDelete = () => {
    let value;
    const state = store.getState();

    if (index !== undefined) {
      const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
      value = Array.isArray(argumentValue) ? [...argumentValue] : [];
      value = value?.filter((v, idx) => idx !== index);
      writeData(argument?.key, value);
    } else {
      const values = argumentSelectors[dataSource].activeArguments()(state);
      const { [argument?.key]: deleted, ...rest } = values;
      writeData(null, rest);
    }
  };

  const handleSelectDelete = ({ key: selectKey, index: selectIndex }) => {
    const state = store.getState();
    const values = argumentSelectors[dataSource].activeArguments()(state);
    let value;
    if (selectIndex !== undefined) {
      value = Array.isArray(values?.[selectKey]) ? [...values?.[selectKey]] : [];
      value = value?.filter((v, idx) => idx !== selectIndex);
      writeData(selectKey, value);
    } else {
      const { [selectKey]: deleted, ...rest } = values;
      writeData(null, rest);
    }
  };

  const handleLexicalChange = useCallback(
    (value) => {
      const label = lookupOptions?.find((item) => item?.value === value)?.label || undefined;
      const type = argument.dataType === 'boolean' ? componentType : value?.type || componentType;
      const val = {
        timezone: value?.timezone ? value.timezone : undefined,
        value: value?.value ? value.value : value,
        type,
        ...(label ? { label } : {})
      };

      let actualValue = val;

      if (index !== undefined) {
        actualValue.id = value?.id || id.current || randomId();
        const state = store.getState();
        const argumentValue = argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key];
        actualValue = Array.isArray(argumentValue) ? [...argumentValue] : [];
        actualValue[index] = val;
      }

      writeData(argument?.key, actualValue);
    },
    [lookupOptions, argument?.key]
  );

  const handleEnterRawObjectClick = (e) => {
    const state = store.getState();
    const values = argumentSelectors[dataSource].activeArguments()(state);
    return handleEnterRawObject(e, argument, values, actions, dataSourcePath, dataSavePath);
  };

  const lexicalActions = [
    {
      icon: DeleteIcon,
      onClick: () => handleDelete(),
      title: 'Delete Argument',
      show: showTrash
    },
    {
      icon: BiText,
      title: 'Edit File Name',
      show: isFile,
      popover: ArgumentFilePopover,
      value: fileName,
      onChange: handleFileNameChange
    },
    {
      icon: PiBracketsCurly,
      title: 'Use Variables',
      show: hasMultipleValues && !argValueExists && index === 0,
      onClick: handleEnterRawObjectClick
    }
  ].filter((action) => action.show);

  const lexicalSuggestions = (() => {
    const result = [];

    if (activeArgument?.dependencies?.length) {
      result.push(suggestions.argumentDependencies);
    }
    if (dataSource === argumentDataSources.appLongPollingActionInstance) {
      result.push(suggestions.longPollingTriggers);
    }
    if (dataSource === argumentDataSources.appLongPollingExtraActionInstance) {
      result.push(suggestions.longPollingTriggers, suggestions.longPollingExtraAction);
    }

    return result.length > 0 ? result : null;
  })();

  useEffect(() => {
    if (!argument?.component) {
      setCustomComponent(null);
      return;
    }

    const Component = React.lazy(() => import(`@/screens/workflows/components/workflowInput/dynamicComponents/${argument?.component}.jsx`));
    setCustomComponent({ name: argument?.component, component: Component });
  }, [argument?.component]);

  return (
    <>
      {['lexical', 'draft', 'default'].includes(componentType) && <LexicalInput key={updateKey} id={id.current} argKey={argument?.key} variation={'workflow'} value={initialValue} placeholder={placeholder} index={index} actions={lexicalActions} argTimezone={argValueTimezone} timezone={timezone} dataSource={dataSource} onChange={handleLexicalChange} onUseOptions={isUsingListVariables ? handleUseOptions : isUsingBooleanVariables ? handleUseToggle : null} onUseOptionsTitle={isUsingBooleanVariables ? 'Use Toggle' : 'Use Options'} onTimezoneChange={handleTimezoneChange} suggestions={lexicalSuggestions} disabled={disabled} multipleValuesIndex={index} />}
      {componentType === 'custom' && customComponent && (
        <Suspense fallback={<div />}>
          <customComponent.component key={id.current} id={id.current} argument={argument} definition={definition} options={lookupOptions} value={initialValue} onChange={handleChange} reload={reload} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} disabled={disabled} lookupActionId={lookupActionId} />
        </Suspense>
      )}
      {componentType === 'select' && <ArgumentSelect key={updateKey} id={id.current} argument={argument} value={initialValue} onChange={handleChange} onDelete={handleSelectDelete} onUseVariables={handleUseVariables} options={lookupOptions} index={index} dataSource={dataSource} disabled={disabled} loading={loading} reload={reload} />}
      {componentType === 'toggle' && (
        <div className={`workflow-select-wrapper`}>
          <Toggle size="sm" key={updateKey} id={id.current} active={booleanToggleInitialValue} onChange={handleToggleChange} disabled={disabled} />
          <div className={'workflow-input-actions'}>
            <IconV2 icon={PiBracketsCurly} title={'Use Variables'} onClick={disabled ? null : handleUseVariablesForBoolean} className={'reload-icon'} />
          </div>
        </div>
      )}
      {!['lexical', 'select', 'toggle', 'custom', 'default'].includes(componentType) && <RightPaneFieldV2 key={updateKey} placeholder={argument.description} value={argValueValue} type={componentType} onChange={handleChange} iconPosition={'left'} options={lookupOptions} loading={loading} disabled={disabled} />}
    </>
  );
});

ArgumentInput.displayName = 'ArgumentInput';

export default ArgumentInput;
